import React, { Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AnkhLogo } from '../components/AnkhLogo';
import { useLanguage } from '../components/LanguageContext';
import { translations } from '../translations';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { InstagramIcon, LinkedinIcon, MailIcon } from 'lucide-react';
export function Home() {
  const {
    language
  } = useLanguage();
  const t = translations.home[language];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence mode="wait" data-id="element-125" data-mp="3ovarsaj5it46">
      <motion.div key={language} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="w-full" data-id="element-126" data-mp="3oyqarofy4mav">
        {/* Hero Split Screen */}
        <div className="split-screen-container" data-id="element-127" data-mp="2mxjnlgwled86">
          {/* Left Image Side */}
          <div className="split-screen-left" data-id="element-128" data-mp="2hxxbjasi330y">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
            backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/wz9NNYmZ46L9zRj8VVQn9s/2ef651e8ec5baac18e48393b78f32e61.jpg")'
          }} data-id="element-129" data-mp="2hyp8aoxu36st" />
            <div className="absolute inset-0 bg-dark/40" data-id="element-130" data-mp="23acb5on0fhwq" />
          </div>

          {/* Right Content Side */}
          <div className="split-screen-right bg-dark !justify-start items-center justify-start" data-id="element-131" data-mp="1z6mlevulmua4">
            <motion.div className="flex flex-col items-center text-center w-full max-w-md mt-[calc(35vh-150px)] md:mt-[calc(38vh-150px)]" variants={containerVariants} initial="hidden" animate="visible" data-id="element-132" data-mp="1u94mlyaapvfu">
              <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl lg:text-6xl font-cinzel mb-12 tracking-wider leading-tight text-[#B89A6F] font-[300]" data-id="element-133" data-mp="1ankvc503mjd7">
                JULIETTE CAZABAT
              </motion.h1>

              <motion.div variants={itemVariants} className="space-y-4 mb-24 w-full" data-id="element-134" data-mp="28atbrll8t28c">
                {t.titles.map((title, idx) => <p key={idx} className="text-rose font-inter text-xs md:text-sm uppercase tracking-widest" data-id="element-135" data-mp="1yki7fxxncr0x" data-mpi={idx}>
                    {title}
                  </p>)}
              </motion.div>

              <motion.div variants={itemVariants} className="mt-auto pt-12" data-id="element-136" data-mp="2r240px7er4xl">
                <AnkhLogo className="w-32 h-32" data-id="element-137" data-mp="fkiz96n1r2sa" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="bg-dark py-24 md:py-32 px-6" data-id="element-138" data-mp="odvzyurcr8ox">
          <div className="max-w-3xl mx-auto text-center" data-id="element-139" data-mp="odcfb6abi677">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1
          }} data-id="element-140" data-mp="kcag2pgehu58">
              <h2 className="text-3xl md:text-4xl font-cinzel text-gold mb-8 font-[300]" data-id="element-141" data-mp="ni4eu9k6rpl">
                {t.introTitle}
              </h2>
              <p className="text-cream/80 md:text-2xl mb-12 font-[Inter,_sans-serif] text-[20px]" data-id="element-142" data-mp="1h4w5ja5c0kip">
                {t.introText}
              </p>
              <Link to="/services" className="inline-block" data-id="element-143" data-mp="27eoiq60qxmrb">
                <button className="btn-outline-gold" data-id="element-144" data-mp="27g9chtf8l1o4">
                  {'Book a free consultation'}
                </button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Carousel */}
        <TestimonialCarousel data-id="element-145" data-mp="2mbfgkauruqet" />

        {/* About Me Section */}
        <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-id="element-146" data-mp="2qcqyrvbbg9cz">
          <div className="max-w-6xl mx-auto" data-id="element-147" data-mp="2qch8e6md006r">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1
          }} data-id="element-148" data-mp="2vbkbvmmoxycm">
              <div className="md:clearfix" data-id="element-149" data-mp="3e1ts40fbbqnb">
                <h2 className="text-3xl md:text-4xl font-cinzel text-[#B89A6F] mb-10 tracking-wider" data-id="element-150" data-mp="3e2lcef3gm1ya">
                  {t.aboutTitle}
                </h2>

                {/* Portrait floats so text wraps naturally around it on desktop */}
                <div className="mb-8 md:mb-4 md:float-left md:mr-10 md:mt-2 w-full md:w-[42%] max-w-md" data-id="element-151" data-mp="te1spm62ohex">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#1a1a1a]" data-id="element-152" data-mp="ofqj9cjow8x8">
                    <img src="https://cdn.magicpatterns.com/uploads/v5DWB986M4QVkGhPDFZFcm/1E148774-1811-44B2-979C-933419BA7043_1_105_c.jpg" alt="Juliette Cazabat portrait" className="w-full h-full object-cover rounded-lg" data-id="element-153" data-mp="1m2pls3x2cb31" />
                  </div>
                </div>

                <div className="text-cream/80 font-[Inter,_sans-serif] text-[18px] leading-[1.7]" data-id="element-154" data-mp="17dt2tbtgxdnn">
                  <p className="mb-6" data-id="element-155" data-mp="24urtgprq8h15">
                    Juliette is a French-born therapist and entrepreneur who has
                    called New Zealand home for more than ten years. With a
                    foundation in psychology and a longstanding interest in
                    holistic healing, her work integrates clinical understanding
                    with embodied and transformational approaches to wellbeing.
                  </p>
                  <p className="mb-8" data-id="element-156" data-mp="3pjcal8a9gsvk">
                    Her professional path has been deeply informed by
                    experiences of cultural transition, identity evolution and
                    motherhood — perspectives that continue to shape her
                    approach to therapy and human connection.
                  </p>

                  <p className="mb-4 text-cream/90" data-id="element-157" data-mp="31nrhbgen54w3">
                    Qualifications and professional training include:
                  </p>
                  <ul className="list-disc pl-6 mb-8 space-y-2 marker:text-[#B89A6F]" data-id="element-158" data-mp="zu0mewwuoazc">
                    <li data-id="element-159" data-mp="14tn2de42c5bu">BSc Psychology (University of Lyon)</li>
                    <li data-id="element-160" data-mp="14svdsn8vv7v2">
                      Advanced studies in holistic healing and somatic
                      approaches
                    </li>
                    <li data-id="element-161" data-mp="19z0hgz8li2dh">
                      Ongoing professional development in relationship and
                      transformational work
                    </li>
                  </ul>

                  <p className="mb-2" data-id="element-162" data-mp="1iynbgzyzw5wi">
                    Over the years, Juliette has supported individuals and
                    couples navigating emotional challenges, relationship
                    dynamics, life transitions, and personal growth. Her
                    approach combines psychological insight, somatic awareness,
                    and intuitive guidance, offering a grounded and
                    compassionate space for meaningful transformation.
                  </p>
                </div>

                <div className="md:clear-both" data-id="element-163" data-mp="cbrqwptqm34m" />
              </div>

              {/* Nexus Dance Paragraph — flows below the grid */}
              <div className="mt-16 max-w-4xl mx-auto" data-id="element-164" data-mp="8b7x9w8dhhn8">
                <p className="text-cream/80 md:text-xl text-[16px] font-[Inter,_sans-serif]" data-id="element-165" data-mp="wt0eclivepry">
                  {t.nexusParagraph}
                </p>

                {/* Explore More — Two Column */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-16 mt-20" data-id="element-166" data-mp="16kxsa019jvud">
                  {/* Left: Nexus Dance */}
                  <div className="flex flex-col items-center text-center" data-id="element-167" data-mp="1ankmu8pbjuv9">
                    <div className="w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-[#B89A6F]/50 shadow-lg mb-8" data-id="element-168" data-mp="dwin057xorw8">
                      <img src="https://cdn.magicpatterns.com/uploads/3YyeuDth3JnNgJDPLQQDFB/WhatsApp_Image_2026-03-08_at_4.00.24_PM.jpg" alt="Nexus Dance School" className="w-full h-full object-cover" data-id="element-169" data-mp="iw50mfroca5k" />
                    </div>
                    <a href="https://nexusdance.wixsite.com/nexus" target="_blank" rel="noopener noreferrer" className="inline-block font-cinzel text-sm tracking-wider px-8 py-3 border border-[#B89A6F] text-[#B89A6F] hover:bg-[#B89A6F] hover:text-[#0D0D0D] transition-all duration-300 rounded-sm" data-id="element-170" data-mp="239m6smdrt0z1">
                      {t.visitNexus}
                    </a>
                  </div>

                  {/* Right: Retreats */}
                  <div className="flex flex-col items-center text-center" data-id="element-171" data-mp="1uodshov835oj">
                    <div className="w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-[#B89A6F]/50 shadow-lg mb-8" data-id="element-172" data-mp="1po890vz3wphj">
                      <img src="https://cdn.magicpatterns.com/uploads/hEiBW9AQDHknXofxTEmk7s/0c1dd352a4a36fd9353471ca70e06926.jpg" alt="Kawau Island retreat landscape" className="w-full h-full object-cover" data-id="element-173" data-mp="1kpomouglpbon" />
                    </div>
                    <Link to="/retreats" className="inline-block font-cinzel text-sm tracking-wider px-8 py-3 border border-[#B89A6F] text-[#B89A6F] hover:bg-[#B89A6F] hover:text-[#0D0D0D] transition-all duration-300 rounded-sm" data-id="element-174" data-mp="col7yktz3dgv">
                      {t.explorePastRetreats}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Professional Social Footer */}
              <div className="mt-20 pt-12 border-t border-cream/10 flex justify-center items-center gap-8" data-id="element-175" data-mp="3q1ew3f0sxzr5">
                <a href="https://www.instagram.com/therapy.nz/" target="_blank" rel="noopener noreferrer" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Juliette's professional Instagram" data-id="element-176" data-mp="3l2k1lfth2mr7">
                  <InstagramIcon className="w-5 h-5" data-id="element-177" data-mp="32eow0j80kf96" />
                </a>
                <a href="https://www.linkedin.com/in/juliette-cazabat-b2504b3a3/" target="_blank" rel="noopener noreferrer" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Juliette's LinkedIn" data-id="element-178" data-mp="1hvur0ytqzgo6">
                  <LinkedinIcon className="w-5 h-5" data-id="element-179" data-mp="1cxkxstrl2q57" />
                </a>
                <a href="mailto:juliettecazabat.nz@gmail.com" className="text-cream/50 hover:text-[#B89A6F] transition-colors duration-300" aria-label="Email Juliette" data-id="element-180" data-mp="20luhnq58l3tp">
                  <MailIcon className="w-5 h-5" data-id="element-181" data-mp="1rl63mvfuf3gt" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </AnimatePresence>;
}